"use client";

import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import { Button } from "@/components/ui/button";
import { Mic, MicOff, ArrowRight } from "lucide-react";
import { useEnv } from "@/context/envContext";
import { ProbeMessage, ProbeRequest, ProbeResponse, ProbeResult } from "@/lib/types/probe.types";
import { addAudioPath } from "@/lib/audio-storage";

const CONSTANTS = {
  SILENCE_THRESHOLD: 4, // in decibels
  SILENCE_DURATION: 3000, // 3 seconds after user responds in milliseconds
  NO_RESPONSE_DURATION: 15000, // 15 seconds if no user response
  CHUNK_DURATION: 3000, // in milliseconds
  SESSION_WARNING_TIME: 12 * 60 * 1000, // 12 minutes in milliseconds
  SESSION_MAX_TIME: 15 * 60 * 1000, // 15 minutes in milliseconds (12 + 3)
  ANSWER_WINDOW_DURATION: 20 * 1000, // 20 seconds answer window in milliseconds
  // SESSION_WARNING_TIME: 30 * 1000, // 30 seconds in milliseconds
  // SESSION_MAX_TIME: 60 * 1000, // 1 minute in milliseconds (12 + 3)
}

type OrbProps = {
  isActive: boolean;
  isMuted: boolean;
  isSpeaking?: boolean;
  level?: number; // 0 - 100
};

function AnimatedOrb({ isActive, isMuted, isSpeaking = false, level = 0 }: OrbProps): JSX.Element {
  const speakingSeeds = React.useMemo(() => (
    Array.from({ length: 3 }).map(() => ({
      delay: `${(Math.random() * 0.8).toFixed(2)}s`,
      duration: `${(1.2 + Math.random() * 0.8).toFixed(2)}s`,
      scale: 1 + Math.random() * 0.35,
      opacity: 0.25 + Math.random() * 0.35,
    }))
  ), [isSpeaking]);

  const pulseClass = isActive && !isMuted ? "animate-[pulse_2.2s_ease-in-out_infinite]" : "";
  const glowClass = isActive && !isMuted ? "shadow-[0_0_80px_20px_rgba(59,130,246,0.45)]" : "shadow-[0_0_40px_8px_rgba(148,163,184,0.25)]";
  const opacity = isMuted ? "opacity-60" : "opacity-100";
  const clamped = isMuted ? 0 : Math.max(0, Math.min(100, level));
  const scale = isSpeaking ? 1.06 : (1 + clamped / 55);
  const rippleOpacity = isSpeaking ? 0.45 : Math.min(0.55, 0.18 + clamped / 200);

  return (
    <div className="relative h-[260px] w-[260px] sm:h-[320px] sm:w-[320px]">
      <div className={`absolute inset-0 rounded-full bg-gradient-to-br from-sky-300 via-blue-400 to-sky-600 blur-3xl ${glowClass} ${opacity}`} />
      <div
        className={[
          "absolute inset-2 rounded-full",
          "bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))]",
          "from-white via-sky-100 to-sky-300",
          "transition-transform duration-700 will-change-transform",
          pulseClass,
        ].join(" ")}
        style={{ transform: `scale(${scale.toFixed(3)})` }}
      />
      {/* dynamic ripples */}
      <div className="absolute inset-0 rounded-full ring-1 ring-white/30" />
      <div
        className="absolute inset-0 rounded-full"
        style={{
          transform: `scale(${(isSpeaking ? 1.1 : (1 + clamped / 120)).toFixed(3)})`,
          boxShadow: `0 0 ${28 + (isSpeaking ? 12 : clamped / 1.6)}px ${12 + (isSpeaking ? 6 : clamped / 7)}px rgba(59,130,246,${rippleOpacity.toFixed(3)})`,
          transition: "transform 300ms ease, box-shadow 300ms ease",
          opacity: isMuted ? 0.5 : 1,
        }}
      />
      {isSpeaking && (
        <>
          {speakingSeeds.map((s, i) => (
            <div
              key={i}
              className="absolute inset-0 rounded-full bg-sky-300/30 animate-ping"
              style={{
                animationDelay: s.delay as any,
                animationDuration: s.duration as any,
                transform: `scale(${s.scale.toFixed(2)})`,
                opacity: s.opacity,
              }}
            />
          ))}
        </>
      )}
    </div>
  );
}

const sanitizeInput = (input: string): string => {
  return input
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#x27;")
    .replace(/\//g, "&#x2F;");
};

async function generateResponse(
  message: string,
  chatHistory: ProbeMessage[],
  probeConfig: Omit<ProbeRequest, "message" | "chat_history">,
  apiUrl: string,
  csrfToken?: string,
  applicationId?: string,
  audioBlob?: Blob,
  interviewLinkId?: string
): Promise<ProbeResponse> {
  const formData = new FormData();
  
  // Add all fields from probeConfig
  Object.entries(probeConfig).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
    }
  });
  
  // Add required fields
  formData.append("message", message);
  formData.append("chat_history", JSON.stringify(chatHistory));
  if (applicationId) {
    formData.append("applicationId", applicationId);
  }
  
  // Add audio file if provided
  if (audioBlob) {
    const audioFile = new File([audioBlob], `audio-${Date.now()}.webm`, { 
      type: audioBlob.type || 'audio/webm' 
    });
    formData.append("audio", audioFile);
  }
  
  const response = await fetch(`${apiUrl}/api/career/probe`, {
    method: "POST",
    headers: {
      // Don't set Content-Type - browser will set it with boundary for multipart
      "X-CSRF-Token": csrfToken || "",
      "X-Interview-Link-Id": interviewLinkId || "",
    },
    body: formData,
    credentials: "include", // Send session cookie
  });
  if (!response.ok) throw new Error("Failed to generate response");
  return response.json();
}

export function AudioInterview({
  probeConfig,
  jobId,
  onComplete,
  csrfToken,
  applicationId,
  interviewLinkId,
}: {
  probeConfig?: Omit<ProbeRequest, "message" | "chat_history">;
  jobId?: string;
  onComplete?: (result: ProbeResult) => void;
  csrfToken?: string;
  applicationId?: string;
  interviewLinkId?: string;
}): JSX.Element {
  const env = useEnv();
  const [isListening, setIsListening] = useState<boolean>(false);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [chatHistory, setChatHistory] = useState<ProbeMessage[]>([]);
  const [textInput, setTextInput] = useState<string>("");
  const [currentTranscription, setCurrentTranscription] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [microphoneActive, setMicrophoneActive] = useState<boolean>(false);
  const [audioLevel, setAudioLevel] = useState<number>(0);
  const [permission, setPermission] = useState<"unknown" | "prompt" | "granted" | "denied">("unknown");
  const [permissionDetail, setPermissionDetail] = useState<"dismissed" | "denied" | "system" | "unknown">("unknown");

  const streamRef = useRef<MediaStream | null>(null);
  const recordingStateRef = useRef<boolean>(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isSpeaking, setIsSpeaking] = useState<boolean>(false);
  const [silenceTimer, setSilenceTimer] = useState<NodeJS.Timeout | null>(null);
  const [lastAudioTime, setLastAudioTime] = useState<number>(0);
  const [isUserStoppedTalking, setIsUserStoppedTalking] = useState<boolean>(false);
  const [hasUserResponded, setHasUserResponded] = useState<boolean>(false);
  const [noResponseTimer, setNoResponseTimer] = useState<NodeJS.Timeout | null>(null);
  const [showNoResponseMessage, setShowNoResponseMessage] = useState<boolean>(false);
  const [pendingSubmission, setPendingSubmission] = useState<boolean>(false);
  const [currentRecorder, setCurrentRecorder] = useState<MediaRecorder | null>(null);
  const [pendingTranscription, setPendingTranscription] = useState<boolean>(false);
  const [submissionInProgress, setSubmissionInProgress] = useState<boolean>(false);
  const [chunkCount, setChunkCount] = useState<number>(0);
  
  // Audio chunk accumulation for combining before sending
  const accumulatedAudioChunksRef = useRef<Blob[]>([]);
  const [combinedAudioBlob, setCombinedAudioBlob] = useState<Blob | undefined>(undefined);
  const [isFreshStart, setIsFreshStart] = useState<boolean>(true);
  const [showWelcomeScreen, setShowWelcomeScreen] = useState<boolean>(true);
  const [autoSendMode, setAutoSendMode] = useState<boolean>(true);
  const handleSubmitRef = useRef<(() => Promise<void>) | null>(null);
  const shouldStopRecordingRef = useRef<boolean>(false);
  const chatHistoryRef = useRef<ProbeMessage[]>([]);
  const latestTranscriptionRef = useRef<string>('');
  
  // Full interview recording (mic + speaker/TTS)
  const fullInterviewRecorderRef = useRef<MediaRecorder | null>(null);
  const fullInterviewAudioChunksRef = useRef<Blob[]>([]);
  const fullInterviewAudioContextRef = useRef<AudioContext | null>(null);
  const fullInterviewMediaStreamRef = useRef<MediaStream | null>(null);
  const fullInterviewDestinationRef = useRef<MediaStreamAudioDestinationNode | null>(null);
  const fullInterviewBlobRef = useRef<Blob | null>(null); // Store the final full interview blob
  const fullInterviewAudioElementSourceRef = useRef<MediaElementAudioSourceNode | null>(null); // Store audio element source

  // Create a wrapper function that's always available
  const executeSubmit = useCallback(() => {
    if (handleSubmitRef.current) {
      handleSubmitRef.current();
    } else {
      console.warn('handleSubmitRef.current is null, submission may be delayed');
      // We'll set up the ref properly in useEffect
    }
  }, []);
  const [userTalkedAfterAI, setUserTalkedAfterAI] = useState<boolean>(false);
  const [silenceStartTime, setSilenceStartTime] = useState<number>(0);
  const [silenceCountdown, setSilenceCountdown] = useState<number>(0);
  
  // Session timer states
  const [sessionStartTime, setSessionStartTime] = useState<number | null>(null);
  const [sessionElapsedTime, setSessionElapsedTime] = useState<number>(0);
  const [showTimeWarning, setShowTimeWarning] = useState<boolean>(false);
  const [hasShownWarning, setHasShownWarning] = useState<boolean>(false);
  
  // Answer window timer states
  const [answerWindowActive, setAnswerWindowActive] = useState<boolean>(false);
  const [answerWindowRemaining, setAnswerWindowRemaining] = useState<number>(0);
  const answerWindowStartTimeRef = useRef<number | null>(null);

  const toggleMute = useCallback((): void => {
    if (showNoResponseMessage) {
      // Restart listening after no response timeout
      setShowNoResponseMessage(false);
      setIsListening(true);
      setIsMuted(false);
      setHasUserResponded(false);
      setLastAudioTime(Date.now());
      shouldStopRecordingRef.current = false;
    } else {
      // Simply toggle mute - transcription data will be preserved by useEffect logic
      setIsMuted((m) => !m);
    }
  }, [showNoResponseMessage]);

  const checkMicrophone = useCallback(() => {
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices.getUserMedia({ audio: true, video: false })
        .then((stream) => {
          setMicrophoneActive(true);
          setPermission("granted");
          setPermissionDetail("unknown");
          const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
          const audioContext = new AudioCtx();
          const source = audioContext.createMediaStreamSource(stream);
          const analyser = audioContext.createAnalyser();
          analyser.fftSize = 256;
          analyser.smoothingTimeConstant = 0.5;
          source.connect(analyser);
          const bufferLength = analyser.frequencyBinCount;
          const dataArray = new Uint8Array(bufferLength);
          const loop = () => {
            if (isMuted) {
              // When muted, set audio level to 0 and don't process audio
              setAudioLevel(0);
              if (microphoneActive) requestAnimationFrame(loop);
              return;
            }
            
            analyser.getByteTimeDomainData(dataArray);
            let sum = 0; let max = 0;
            for (let i = 0; i < bufferLength; i++) {
              const v = Math.abs(dataArray[i] - 128);
              sum += v; max = Math.max(max, v);
            }
            const avg = sum / bufferLength;
            const scaled = Math.min(100, Math.max(0, (avg * 2) + (max / 2)));
            setAudioLevel((prev) => {
              const currentLevel = Math.round((prev * 0.3) + (scaled * 0.7));
              return currentLevel;
            });
            
            if (microphoneActive) requestAnimationFrame(loop);
          };
          loop();
          (window as any).microphoneStream = stream;
        })
        .catch((err) => {
          console.error("Error accessing microphone:", err);
          const name = (err && (err.name || err.code)) || "";
          const msg = (err && err.message ? String(err.message).toLowerCase() : "");
          if (name === "NotAllowedError") {
            if (msg.includes("denied")) {
              setPermission("denied");
              setPermissionDetail("denied");
            } else if (msg.includes("dismissed") || msg.includes("permission dismissed")) {
              // User closed the prompt; allow retry which will re-show the prompt
              setPermission("prompt");
              setPermissionDetail("dismissed");
            } else {
              // Treat as denied by default
              setPermission("denied");
              setPermissionDetail("unknown");
            }
          } else if (name === "NotFoundError") {
            setPermission("denied");
            setPermissionDetail("system");
          } else {
            setPermission("prompt");
            setPermissionDetail("unknown");
          }
        });
    }
  }, [microphoneActive]);

  // Attempt to auto-enable mic on mount. If denied or blocked, show UI prompt.
  useEffect(() => {
    const tryAuto = async () => {
      try {
        // Some browsers support permissions API
        const navAny: any = navigator as any;
        if (navAny?.permissions?.query) {
          const status = await navAny.permissions.query({ name: "microphone" as PermissionName });
          setPermission(status.state as any);
          if ((status.state === "granted" || status.state === "prompt") && permission !== "denied") {
            // Trigger prompt automatically on first load
            checkMicrophone();
          }
          status.onchange = () => setPermission((status.state as any) || "unknown");
        } else {
          // Fallback: try requesting directly
          if (permission !== "denied") checkMicrophone();
        }
      } catch {
        // Fallback on any error
        if (permission !== "denied") checkMicrophone();
      }
    };
    tryAuto();
  }, [checkMicrophone, permission]);

  // Sync listening state with mic + mute
  useEffect(() => {
    setIsListening(microphoneActive && !isMuted);
  }, [microphoneActive, isMuted]);

  useEffect(() => () => {
    const s: any = (window as any).microphoneStream;
    if (s) s.getTracks().forEach((t: MediaStreamTrack) => t.stop());
  }, []);

  const sendAudioChunk = useCallback(async (audioBlob: Blob, opts?: { force?: boolean }): Promise<string | null> => {
    const force = Boolean(opts?.force);
    if (!force) {
      if (!isListening) {
        return null;
      }
      if (isMuted) {
        return null;
      }
      // Don't transcribe when TTS is speaking to prevent echo transcription
      if (isSpeaking) {
        return null;
      }
    }
    if (!audioBlob || audioBlob.size <= 100) {
      return null;
    }
    
    // Allow multiple concurrent transcriptions for continuous speech recognition
    // (Removed the pendingTranscription check to allow continuous transcription)
    
    // Don't transcribe if user has stopped talking (unless we need the final transcription)
    if (!force) {
      if (isUserStoppedTalking && !pendingSubmission) {
        return null;
      }
    }
    
    try {
      const formData = new FormData();
      formData.append("audio", audioBlob, "audio.webm");
      
      // Add interview link ID if available (required for validation)
      if (interviewLinkId) {
        formData.append("interviewLinkId", interviewLinkId);
      }
      
      const response = await fetch(`${env.NEXT_PUBLIC_EXTERNAL_API_URL}/api/heygen/transcribe`, {
        method: "POST",
        body: formData,
      });
      if (!response.ok) {
        return null;
      }
      const data = await response.json();
      if (data.transcription) {
        const newText = String(data.transcription).trim();
        
        // Accumulate audio chunks for later combination
        if (audioBlob && audioBlob.size > 100) {
          accumulatedAudioChunksRef.current.push(audioBlob);
        }
        
        let updatedTranscription = '';
        setCurrentTranscription((prev) => {
          updatedTranscription = prev ? `${prev} ${newText}` : newText;
          setTextInput(updatedTranscription);
          // Store in ref for immediate access
          latestTranscriptionRef.current = updatedTranscription;
          return updatedTranscription;
        });
        // Return the updated transcription for immediate use
        return updatedTranscription || newText;
      } else {
        return null;
      }
    } catch (e) {
      console.error("sendAudioChunk error", e);
      return null;
    }
  }, [env.NEXT_PUBLIC_EXTERNAL_API_URL, isMuted, isListening, isUserStoppedTalking, pendingSubmission, isSpeaking]);

  const setupRecordingAudioLevelDetection = useCallback((stream: MediaStream) => {
    try {
      const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
      const audioContext = new AudioCtx();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.5;
      source.connect(analyser);
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      
      const loop = () => {
        if (!isListening || !recordingStateRef.current) return;
        
        analyser.getByteTimeDomainData(dataArray);
        let sum = 0; let max = 0;
        for (let i = 0; i < bufferLength; i++) {
          const v = Math.abs(dataArray[i] - 128);
          sum += v; max = Math.max(max, v);
        }
        const avg = sum / bufferLength;
        const scaled = Math.min(100, Math.max(0, (avg * 2) + (max / 2)));
        setAudioLevel((prev) => {
          // If muted, treat as silent but continue silence detection
          const currentLevel = isMuted ? 0 : Math.round((prev * 0.3) + (scaled * 0.7));
          
          // Silence detection logic - process regardless of mute state
          // Reset silence timer when TTS is playing
          if (isSpeaking) {
            setSilenceStartTime(0);
            setSilenceCountdown(0);
            return currentLevel;
          }
          
          const now = Date.now();
          const SILENCE_THRESHOLD = CONSTANTS.SILENCE_THRESHOLD;
          
          // Use actual audio level for detection, but if muted, treat as silent
          const detectionLevel = isMuted ? 0 : currentLevel;
          
          if (detectionLevel > SILENCE_THRESHOLD && !isMuted) {
            // User is speaking (and not muted)
            setLastAudioTime(now);
            setIsUserStoppedTalking(false);
            setHasUserResponded(true);
            setShowNoResponseMessage(false);
            setUserTalkedAfterAI(true); // Mark that user has talked after AI
            setSilenceStartTime(0); // Reset silence start time
            setSilenceCountdown(0); // Reset countdown
            
            // Clear any existing timers
            if (silenceTimer) {
              clearTimeout(silenceTimer);
              setSilenceTimer(null);
            }
            if (noResponseTimer) {
              clearTimeout(noResponseTimer);
              setNoResponseTimer(null);
            }
          } else {
            // User is silent (or muted)
            const timeSinceLastAudio = now - lastAudioTime;
            
            if (userTalkedAfterAI) {
              // User has talked after AI - just track for visual countdown
              // Submission will be handled by lastAudioTime check in chunk completion
              if (silenceStartTime === 0) {
                setSilenceStartTime(now);
              } else {
                const silenceDuration = now - silenceStartTime;
                // Set isUserStoppedTalking when silence duration is reached
                if (silenceDuration >= CONSTANTS.SILENCE_DURATION && !isUserStoppedTalking) {
                  setIsUserStoppedTalking(true);
                }
              }
            } else {
              // User hasn't talked after AI, check for 15-second timeout
              if (timeSinceLastAudio > CONSTANTS.NO_RESPONSE_DURATION && !isUserStoppedTalking) {
                // No user response after 15 seconds - show placeholder and stop recording
                setShowNoResponseMessage(true);
                setIsListening(false); // Stop the recording loop
                shouldStopRecordingRef.current = true; // Immediate stop flag
                const timer = setTimeout(() => {
                  if (!hasUserResponded && !isMuted && isListening) {
                    // Could add a specific action here if needed
                  }
                }, 1000);
                setNoResponseTimer(timer);
              }
            }
          }
          
          return currentLevel;
        });
        
        if (isListening && recordingStateRef.current) requestAnimationFrame(loop);
      };
      loop();
    } catch (err) {
      console.error("Error setting up recording audio level detection:", err);
    }
  }, [isListening, isMuted, hasUserResponded, isUserStoppedTalking, lastAudioTime, silenceTimer, noResponseTimer, currentRecorder, pendingSubmission, userTalkedAfterAI, silenceStartTime]);

  const startNewRecording = useCallback(() => {
    // Don't start recording when TTS is speaking
    if (isSpeaking) return;
    if (!streamRef.current || !recordingStateRef.current || pendingSubmission || shouldStopRecordingRef.current) return;
    const chunks: Blob[] = [];
    const options: MediaRecorderOptions = { mimeType: "audio/webm" } as any;
    try {
      const recorder = new MediaRecorder(streamRef.current as MediaStream, options);
      setCurrentRecorder(recorder);
      
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      recorder.onstop = async () => {
        let freshTranscription: string | null = null;
        
        // First, send the audio chunk and wait for transcription to complete
        if (chunks.length) {
          // Force transcription of the final chunk even if muted/not listening
          freshTranscription = await sendAudioChunk(new Blob(chunks, { type: "audio/webm" }), { force: true });
          // Add small delay to ensure transcription state updates are applied
          await new Promise(resolve => setTimeout(resolve, 200));
        }
        
        // Check if we should submit after this chunk (with fresh transcription)
        const now = Date.now();
        const timeSinceLastAudio = now - lastAudioTime;
        const shouldSubmitByTime = autoSendMode && userTalkedAfterAI && !isSpeaking && timeSinceLastAudio >= 3000;
        const shouldSubmitByPending = pendingSubmission;
        const shouldSubmitByStoppedTalking = autoSendMode && isUserStoppedTalking;
        
        if (shouldSubmitByTime || shouldSubmitByPending || shouldSubmitByStoppedTalking) {
          setCurrentRecorder(null);
          
          // Use fresh transcription if available, otherwise fall back to state
          const transcriptionToUse = freshTranscription || currentTranscription || textInput;
          
          // Store fresh transcription in ref and state for immediate use by handleSubmit
          if (freshTranscription) {
            // Update both state and ref immediately
            setTextInput(freshTranscription);
            setCurrentTranscription(freshTranscription);
            latestTranscriptionRef.current = freshTranscription;
          }
          
          // Clear pending submission and mark submission in progress to prevent double calls
          setPendingSubmission(false);
          setSubmissionInProgress(true);
          
          // Transcription is now complete, submit immediately
          executeSubmit();
          return;
        }
      };
      recorder.start();
      setChunkCount(prev => prev + 1);
      setTimeout(() => {        
        if (recorder.state === "recording" && recordingStateRef.current && isListening && !shouldStopRecordingRef.current) {
          recorder.stop(); 
          // Continue recording as long as we're listening, not explicitly stopped, and TTS is not speaking
          if (recordingStateRef.current && isListening && !shouldStopRecordingRef.current && !isSpeaking) {
            startNewRecording();
          }
        }
      }, CONSTANTS.CHUNK_DURATION);
    } catch (err) {
      console.error("MediaRecorder error", err);
    }
  }, [sendAudioChunk, pendingSubmission, isListening, isMuted, executeSubmit, isSpeaking]);

  useEffect(() => {
    if (isListening) {
      recordingStateRef.current = true;
      
      // Only clear transcription data on fresh start, not when unmuting
      if (isFreshStart) {
        setCurrentTranscription("");
        setTextInput("");
        setChunkCount(0);
        setIsFreshStart(false); // Mark that we're no longer in fresh start
        // Clear accumulated audio chunks on fresh start
        accumulatedAudioChunksRef.current = [];
        setCombinedAudioBlob(undefined);
      }
      
      setIsUserStoppedTalking(false);
      setHasUserResponded(false);
      setShowNoResponseMessage(false);
      setPendingSubmission(false);
      setSubmissionInProgress(false);
      setCurrentRecorder(null);
      shouldStopRecordingRef.current = false;
      setUserTalkedAfterAI(false);
      setSilenceStartTime(0);
      setSilenceCountdown(0);
      setLastAudioTime(Date.now());
      // Clear any existing timers
      if (silenceTimer) {
        clearTimeout(silenceTimer);
        setSilenceTimer(null);
      }
      if (noResponseTimer) {
        clearTimeout(noResponseTimer);
        setNoResponseTimer(null);
      }
      if (!streamRef.current) {
        navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, sampleRate: 16000, echoCancellation: true, noiseSuppression: true }, video: false })
          .then((stream) => { 
            streamRef.current = stream; 
            // Set up audio level detection for the recording stream
            setupRecordingAudioLevelDetection(stream);
            startNewRecording(); 
          })
          .catch((err) => { console.error("getUserMedia error", err); recordingStateRef.current = false; });
      } else {
        startNewRecording();
      }
    } else {
      recordingStateRef.current = false;
      if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
      // Clear timers when stopping
      if (silenceTimer) {
        clearTimeout(silenceTimer);
        setSilenceTimer(null);
      }
      if (noResponseTimer) {
        clearTimeout(noResponseTimer);
        setNoResponseTimer(null);
      }
    }
    return () => {
      recordingStateRef.current = false;
      if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
      // Cleanup timers
      if (silenceTimer) {
        clearTimeout(silenceTimer);
        setSilenceTimer(null);
      }
      if (noResponseTimer) {
        clearTimeout(noResponseTimer);
        setNoResponseTimer(null);
      }
    };
  }, [isListening, startNewRecording, silenceTimer, noResponseTimer, isFreshStart]);

  // Resume recording when TTS finishes speaking
  useEffect(() => {
    if (!isSpeaking && isListening && streamRef.current && recordingStateRef.current && !currentRecorder) {
      // TTS just finished, resume recording
      startNewRecording();
    }
  }, [isSpeaking, isListening, currentRecorder, startNewRecording]);

  // Update countdown timer based on lastAudioTime
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    
    if (autoSendMode && userTalkedAfterAI && !isUserStoppedTalking && !isSpeaking) {
      interval = setInterval(() => {
        const now = Date.now();
        const timeSinceLastAudio = now - lastAudioTime;
        const remaining = Math.max(0, 3000 - timeSinceLastAudio);
        setSilenceCountdown(Math.ceil(remaining / 1000));
        
        if (remaining <= 0) {
          setSilenceCountdown(0); 
          // Mark that user has stopped talking and ensure submission happens
          setIsUserStoppedTalking(true);
          setIsListening(false);
          shouldStopRecordingRef.current = true;
          
          // Check if there's an active recorder
          if (currentRecorder && currentRecorder.state === 'recording') {
            setPendingSubmission(true);
          } else {
            // No pending transcription or recorder, submit immediately
            setSubmissionInProgress(true);
            setTimeout(() => {
              executeSubmit();
            }, 300);
          }
          
          // Additional failsafe - if nothing happens in 2 seconds, force submit
          setTimeout(() => {
            if (isUserStoppedTalking && !isSubmitting && !submissionInProgress) {
              setSubmissionInProgress(true);
              executeSubmit();
            }
          }, 2000);
        }
      }, 100); // Update every 100ms for smooth countdown
    } else {
      setSilenceCountdown(0);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoSendMode, userTalkedAfterAI, isUserStoppedTalking, isSpeaking, lastAudioTime, executeSubmit]);

  // Note: Removed transcription completion monitoring since we now allow continuous transcription
  // Submission is handled by recorder.onstop or countdown timer

  const speakText = useCallback(async (text: string) => {
    try {
      // Stop any active recording when TTS starts
      if (currentRecorder && currentRecorder.state === 'recording') {
        currentRecorder.stop();
        setCurrentRecorder(null);
      }
      
      // Mute microphone when TTS starts playing
      setIsMuted(true);
      setHasUserResponded(false);
      setShowNoResponseMessage(false);
      setUserTalkedAfterAI(false);
      setSilenceStartTime(0);
      setSilenceCountdown(0);
      
      const response = await fetch(`${env.NEXT_PUBLIC_EXTERNAL_API_URL}/api/career/speak`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text,
          voice: "alloy",
          responseFormat: "mp3",
          model: "gpt-4o-mini-tts",
          interviewLinkId,
        }),
      });
      if (!response.ok) return;
      
      // Check content type to handle both JSON (success) and blob (fallback) responses
      const contentType = response.headers.get("content-type") || "";
      let audioUrl: string;
      
      if (contentType.includes("application/json")) {
        // Handle JSON response with audio path and presigned URL
        const data = await response.json();
        
        // Save audio path to IndexedDB if jobId is available
        if (data?.data?.audioPath && jobId) {
          try {
            await addAudioPath(jobId, data.data.audioPath);
          } catch (error) {
            console.error("Failed to save TTS audio path to IndexedDB:", error);
            // Continue even if IDB save fails
          }
        }
        
        // Use presigned URL for playback
        audioUrl = data?.data?.presignedUrl;
        if (!audioUrl) {
          console.error("No presigned URL in TTS response");
          return;
        }
      } else {
        // Fallback: handle raw audio blob (if OSS upload failed)
      const blob = await response.blob();
        audioUrl = URL.createObjectURL(blob);
      }
      
      if (!audioRef.current) audioRef.current = new Audio();
      const el = audioRef.current as HTMLAudioElement;
      // Clean previous object URL if any
      try { if (el.src && el.src.startsWith("blob:")) URL.revokeObjectURL(el.src); } catch {}
      el.src = audioUrl;
      setIsSpeaking(true);
      
       // Connect TTS audio to full interview recording if recording is active
       // IMPORTANT: MediaElementSource disconnects audio from default output
       // We need to connect it to BOTH recording destination AND speakers
       if (fullInterviewRecorderRef.current && fullInterviewRecorderRef.current.state === 'recording' && fullInterviewDestinationRef.current && fullInterviewAudioContextRef.current) {
         try {
           // Create MediaElementSource if it doesn't exist yet
           // (audio element might not have existed when startFullInterviewRecording was called)
           if (!fullInterviewAudioElementSourceRef.current) {
             console.log('🎙️ Creating MediaElementSource for TTS audio (in speakText)');
             fullInterviewAudioElementSourceRef.current = fullInterviewAudioContextRef.current.createMediaElementSource(el);
           }
           
           // Disconnect from all previous connections
           try {
             fullInterviewAudioElementSourceRef.current.disconnect();
           } catch (e) {
             // Ignore if not connected
           }
           
           // Connect to recording destination (for capturing)
           fullInterviewAudioElementSourceRef.current.connect(fullInterviewDestinationRef.current);
           
           // Connect to audio context destination (for speakers - so user can hear)
           fullInterviewAudioElementSourceRef.current.connect(fullInterviewAudioContextRef.current.destination);
           
           console.log('🎙️ TTS audio connected to BOTH recording AND speakers');
         } catch (error) {
           console.error('Error connecting TTS audio:', error);
         }
       }
      // clean previous listeners
      el.onended = () => {
        setIsSpeaking(false);
        // Re-enable microphone after TTS finishes
        setIsMuted(false);
        setLastAudioTime(Date.now());
        
        // Start 20-second answer window timer (only if not already active)
        if (!answerWindowStartTimeRef.current) {
          answerWindowStartTimeRef.current = Date.now();
          setAnswerWindowActive(true);
          setAnswerWindowRemaining(CONSTANTS.ANSWER_WINDOW_DURATION);
        }
      };
      el.onpause = () => {
        setIsSpeaking(false);
        setIsMuted(false);
        setLastAudioTime(Date.now());
      };
      el.onerror = () => {
        setIsSpeaking(false);
        setIsMuted(false);
        setLastAudioTime(Date.now());
      };
      await el.play();
    } catch (e) {
      console.error("speakText error", e);
      // Re-enable microphone on error
      setIsMuted(false);
      setLastAudioTime(Date.now());
    }
  }, [env.NEXT_PUBLIC_EXTERNAL_API_URL, currentRecorder]);

  // Stop full interview recording and wait for blob to be ready
  const stopFullInterviewRecording = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      if (fullInterviewRecorderRef.current && fullInterviewRecorderRef.current.state !== 'inactive') {
        // Store the original onstop handler
        const originalOnStop = fullInterviewRecorderRef.current.onstop;
        
        // Create a wrapper that calls the original handler and then waits for blob
        fullInterviewRecorderRef.current.onstop = async (event?: Event) => {
          // Call original handler if it exists (it sets fullInterviewBlobRef.current)
          if (originalOnStop && typeof originalOnStop === 'function') {
            try {
              // Call the original handler (it's async, so await it)
              await (originalOnStop as any)(event);
            } catch (error) {
              console.error('Error in original onstop handler:', error);
            }
          }
          
          // Wait for the blob to be set in the ref (original handler sets it)
          const checkBlob = () => {
            if (fullInterviewBlobRef.current) {
              resolve();
            } else {
              // If blob not ready yet, wait a bit more (max 5 seconds)
              const maxWait = 5000;
              const startTime = Date.now();
              if (Date.now() - startTime < maxWait) {
                setTimeout(checkBlob, 100);
              } else {
                console.warn('⚠️ Timeout waiting for full interview blob');
                resolve(); // Resolve anyway to not block
              }
            }
          };
          
          // Start checking for blob after a short delay to let original handler run
          setTimeout(checkBlob, 100);
        };
        
        fullInterviewRecorderRef.current.stop();
      } else {
        // If recorder is already stopped or doesn't exist, resolve immediately
        resolve();
      }
    });
  }, []);

  const handleSubmit = useCallback(async () => {
  
    // Prevent double submissions
    if (isSubmitting || submissionInProgress) {
      return;
    }
    
    // Short delay to ensure state updates are applied
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Use the latest transcription from ref, fallback to state
    const latestText = latestTranscriptionRef.current || textInput || currentTranscription;
    const finalText = latestText.trim();
    if (!finalText || finalText.length < 2) {
      // Reset states and continue listening
      setIsUserStoppedTalking(false);
      setHasUserResponded(false);
      setSilenceStartTime(0);
      setSilenceCountdown(0);
      // Clear accumulated audio chunks
      accumulatedAudioChunksRef.current = [];
      setCombinedAudioBlob(undefined);
      setTimeout(() => setIsListening(true), 1000);
      return;
    }
    
    // Combine all accumulated audio chunks into one blob
    let combinedAudio: Blob | undefined = undefined;
    if (accumulatedAudioChunksRef.current.length > 0) {
      combinedAudio = new Blob(accumulatedAudioChunksRef.current, { type: 'audio/webm' });
      setCombinedAudioBlob(combinedAudio);
    }
    
    // Use the ref to get the latest chat history
    const latestChatHistory = chatHistoryRef.current;
    
    // Check if this is the first message and we should have chat history
    if (latestChatHistory.length === 0 && finalText.toLowerCase() !== 'hello') {
      console.warn('Submitting with empty chat history - this might be an auto-submit issue');
    }
    
    const sanitized = sanitizeInput(finalText);
    setIsSubmitting(true);
    setIsListening(false);
    try {
      const response = await generateResponse(
        sanitized,
        latestChatHistory,
        (probeConfig || { job_id: jobId || "audio-only", candidate_name: "Guest" }) as any,
        env.NEXT_PUBLIC_EXTERNAL_API_URL || "",
        csrfToken,
        applicationId,
        combinedAudio, // Pass combined audio blob
        interviewLinkId // Pass interview link ID
      );
      // Save audio path to IndexedDB if present
      if (response?.audioPath && jobId) {
        try {
          await addAudioPath(jobId, response.audioPath);
        } catch (error) {
          console.error("Failed to save audio path to IndexedDB:", error);
        }
      }
      
      if (response?.data?.chat_history) {
        setChatHistory(response.data.chat_history);
        chatHistoryRef.current = response.data.chat_history;
        // Speak last assistant message if available
        try {
          const history: ProbeMessage[] = response.data.chat_history || [];
          const lastAssistant = [...history].reverse().find((m) => m.role === "assistant");
          const content = (lastAssistant?.content || "").toString();
          if (content.trim()) await speakText(content);
        } catch (e) {
          console.error("TTS play failed", e);
        }
      }
      if (
        response?.data?.is_conversation_stop === "true" ||
        response?.data?.is_conversation_stop === "yes"
      ) {
        // Stop full interview recording and wait for blob to be ready
        await stopFullInterviewRecording();
        
        if (response.data.result && onComplete) {
          // Pass the full interview blob along with the result
          onComplete({ 
            ...response.data.result, 
            chat_history: response.data.chat_history,
            fullInterviewBlob: fullInterviewBlobRef.current || undefined,
          } as any);
        }
      }
      setTimeout(() => setIsListening(true), response?.duration_ms || 1200);
    } catch (e) {
      console.error("generateResponse error", e);
    } finally {
      setIsSubmitting(false);
      setSubmissionInProgress(false); // Reset submission flag
      setTextInput("");
      setCurrentTranscription("");
      latestTranscriptionRef.current = ""; // Reset the ref too
      setIsUserStoppedTalking(false);
      setHasUserResponded(false);
      setShowNoResponseMessage(false);
      setPendingSubmission(false);
      setCurrentRecorder(null);
      setUserTalkedAfterAI(false);
      setSilenceStartTime(0);
      setSilenceCountdown(0);
      setLastAudioTime(Date.now());
      setIsFreshStart(true); // Reset for next fresh start
      
      // Clear accumulated audio chunks after submission
      accumulatedAudioChunksRef.current = [];
      setCombinedAudioBlob(undefined);
    }
  }, [textInput, currentTranscription, chatHistory, env.NEXT_PUBLIC_EXTERNAL_API_URL, csrfToken, probeConfig, jobId, onComplete, speakText, stopFullInterviewRecording]);

  // Initialize handleSubmitRef after handleSubmit is defined
  useEffect(() => {
    handleSubmitRef.current = handleSubmit;
  }, [handleSubmit]);

  // Session timer - tracks total interview time
  useEffect(() => {
    if (!sessionStartTime) return;
    
    const interval = setInterval(() => {
      const elapsed = Date.now() - sessionStartTime;
      setSessionElapsedTime(elapsed);
      
      // Show warning at 12 minutes
      if (elapsed >= CONSTANTS.SESSION_WARNING_TIME && !hasShownWarning) {
        setShowTimeWarning(true);
        setHasShownWarning(true);
      }
      
      // Auto-submit at 15 minutes
      if (elapsed >= CONSTANTS.SESSION_MAX_TIME) {
        clearInterval(interval);
        
        // Stop recording and submit
        setIsListening(false);
        shouldStopRecordingRef.current = true;
        
        // If there's any current transcription, submit it
        if (currentTranscription || textInput || latestTranscriptionRef.current) {
          setTimeout(() => {
            executeSubmit();
          }, 500);
        } else {
          // Force completion even without text - just stop the interview
          setShowTimeWarning(false);
          // Stop recording and wait for blob, then complete
          stopFullInterviewRecording().then(() => {
            // Call onComplete with full interview blob if available
            if (onComplete && fullInterviewBlobRef.current) {
              onComplete({
                chat_history: chatHistory,
                fullInterviewBlob: fullInterviewBlobRef.current,
              } as any);
            }
          });
        }
      }
    }, 1000); // Update every second
    
    return () => clearInterval(interval);
  }, [sessionStartTime, hasShownWarning, currentTranscription, textInput, chatHistory, executeSubmit, onComplete, stopFullInterviewRecording]);

  // Answer window timer - 20 seconds for user to respond after AI speaks
  // Timer keeps counting down regardless of whether user is speaking
  useEffect(() => {
    if (!answerWindowActive || !answerWindowStartTimeRef.current) return;
    
    const interval = setInterval(() => {
      const elapsed = Date.now() - (answerWindowStartTimeRef.current || Date.now());
      const remaining = Math.max(0, CONSTANTS.ANSWER_WINDOW_DURATION - elapsed);
      setAnswerWindowRemaining(remaining);
      
      // When time runs out
      if (remaining <= 0) {
        clearInterval(interval);
        setAnswerWindowActive(false);
        answerWindowStartTimeRef.current = null; // Reset the ref
        
        // If user has started talking, submit what they said
        if (currentTranscription || textInput || latestTranscriptionRef.current) {
          setIsListening(false);
          shouldStopRecordingRef.current = true;
          setTimeout(() => {
            executeSubmit();
          }, 500);
        } else {
          // No response at all - submit empty or skip
          // Optionally, you could submit an empty response or move to next question
          setIsListening(false);
        }
      }
    }, 100); // Update every 100ms for smooth countdown
    
    return () => clearInterval(interval);
  }, [answerWindowActive, currentTranscription, textInput, executeSubmit]);
  
  // Stop answer window timer only when submitting (not when user talks)
  useEffect(() => {
    if (isSubmitting && answerWindowActive) {
      setAnswerWindowActive(false);
      answerWindowStartTimeRef.current = null; // Reset the ref for next question
    }
  }, [isSubmitting, answerWindowActive]);

  // Note: Removed automatic AI interview initialization
  // AI interview will now only start when user clicks "Begin Audio Interview"

  // Start full interview recording (mic + speaker/TTS)
  const startFullInterviewRecording = useCallback(async () => {
    try {
      // Get microphone stream
      const micStream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
        } 
      });
      
      // Create audio context for mixing
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const destination = audioContext.createMediaStreamDestination();
      
      // Connect microphone to destination
      const micSource = audioContext.createMediaStreamSource(micStream);
      micSource.connect(destination);
      
      // Connect TTS audio (from audio element) to destination if available
      // Note: createMediaElementSource can only be called once per audio element
      // IMPORTANT: When creating MediaElementSource, audio is disconnected from default output
      // We must connect it to BOTH recording destination AND speakers
      if (audioRef.current && !fullInterviewAudioElementSourceRef.current) {
        try {
          const audioElementSource = audioContext.createMediaElementSource(audioRef.current);
          
          // Connect to recording destination (for capturing)
          audioElementSource.connect(destination);
          
          // Connect to audio context destination (for speakers - so user can hear)
          audioElementSource.connect(audioContext.destination);
          
          fullInterviewAudioElementSourceRef.current = audioElementSource; // Store for reuse
          console.log('🎙️ Connected TTS audio to BOTH recording AND speakers (initial setup)');
        } catch (error) {
          console.warn('Could not connect TTS audio to recording:', error);
        }
      } else if (fullInterviewAudioElementSourceRef.current && fullInterviewDestinationRef.current) {
        // Reconnect existing source if it was disconnected
        try {
          // Disconnect from all previous connections
          try {
            fullInterviewAudioElementSourceRef.current.disconnect();
          } catch (e) {
            // Ignore if not connected
          }
          
          // Connect to recording destination (for capturing)
          fullInterviewAudioElementSourceRef.current.connect(fullInterviewDestinationRef.current);
          
          // Connect to audio context destination (for speakers - so user can hear)
          fullInterviewAudioElementSourceRef.current.connect(audioContext.destination);
          
          console.log('🎙️ Reconnected TTS audio to BOTH recording AND speakers');
        } catch (error) {
          console.warn('Could not reconnect TTS audio source:', error);
        }
      }
      
      // Create MediaRecorder for the mixed stream
      const mixedStream = destination.stream;
      const recorder = new MediaRecorder(mixedStream, { mimeType: 'audio/webm' });
      
      const chunks: Blob[] = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };
      
      recorder.onstop = async () => {
        if (chunks.length > 0) {
          const fullRecordingBlob = new Blob(chunks, { type: 'audio/webm' });
          fullInterviewAudioChunksRef.current = [fullRecordingBlob];
          // Store the blob for later use in complete API
          fullInterviewBlobRef.current = fullRecordingBlob;
        }
        
        // Cleanup
        if (fullInterviewAudioElementSourceRef.current) {
          try {
            fullInterviewAudioElementSourceRef.current.disconnect();
          } catch (error) {
            // Ignore disconnect errors
          }
          fullInterviewAudioElementSourceRef.current = null;
        }
        if (fullInterviewAudioContextRef.current) {
          fullInterviewAudioContextRef.current.close();
          fullInterviewAudioContextRef.current = null;
        }
        if (micStream) {
          micStream.getTracks().forEach(track => track.stop());
        }
      };
      
      recorder.start();
      
      // Store refs
      fullInterviewRecorderRef.current = recorder;
      fullInterviewAudioContextRef.current = audioContext;
      fullInterviewMediaStreamRef.current = micStream;
      fullInterviewDestinationRef.current = destination;
      
    } catch (error) {
      console.error('Error starting full interview recording:', error);
    }
  }, []);
  
  const startInterview = useCallback(async () => {
    setShowWelcomeScreen(false);
    
    // Start session timer
    setSessionStartTime(Date.now());
    setSessionElapsedTime(0);
    setShowTimeWarning(false);
    setHasShownWarning(false);
    
    // Start full interview recording
    setTimeout(() => {
      startFullInterviewRecording();
    }, 1000); // Wait a bit for audio context to be ready
    
    // Initialize AI interview - AI asks first question
    if (chatHistory.length === 0) {
      // Small delay to ensure microphone is ready
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Send initial message to get AI's first question
      try {
        const response = await generateResponse(
          "Hello",
          [],
          (probeConfig || { job_id: jobId || "audio-only", candidate_name: "Guest" }) as any,
          env.NEXT_PUBLIC_EXTERNAL_API_URL || "",
          csrfToken,
          applicationId,
          undefined, // No audio for initial greeting
          interviewLinkId // Pass interview link ID
        );
        
        if (response?.data?.chat_history) {
          setChatHistory(response.data.chat_history);
          chatHistoryRef.current = response.data.chat_history;
          
          // Speak AI's first message
          try {
            const history: ProbeMessage[] = response.data.chat_history || [];
            const lastAssistant = [...history].reverse().find((m) => m.role === "assistant");
            const content = (lastAssistant?.content || "").toString();
            if (content.trim()) {
              await speakText(content);
            }
          } catch (e) {
            console.error("TTS play failed for initial message", e);
          }
        }
      } catch (e) {
        console.error("Failed to initialize interview", e);
      }
    }
    
    // Start listening after AI finishes speaking
    setTimeout(() => {
      setIsListening(true);
    }, 2000); // Give AI time to finish speaking
  }, [chatHistory.length, probeConfig, jobId, env.NEXT_PUBLIC_EXTERNAL_API_URL, csrfToken, speakText, startFullInterviewRecording]);

  const placeholder = useMemo(() => {
    if (isListening) {
      if (isUserStoppedTalking) {
        return "Processing your response...";
      }
      if (showNoResponseMessage) {
        return "No response detected. Please speak or type your answer...";
      }
      return "Listening…";
    }
    if (showNoResponseMessage) {
      return "No response detected. Click the microphone to try again...";
    }
    return microphoneActive ? (isMuted ? "Muted" : "Ready") : "Enable microphone to begin";
  }, [isListening, microphoneActive, isMuted, isUserStoppedTalking, showNoResponseMessage]);

  // Format elapsed time as MM:SS
  const formatTime = useCallback((milliseconds: number): string => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, []);

  // Calculate remaining time until max
  const remainingTime = useMemo(() => {
    if (!sessionStartTime) return CONSTANTS.SESSION_MAX_TIME;
    return Math.max(0, CONSTANTS.SESSION_MAX_TIME - sessionElapsedTime);
  }, [sessionStartTime, sessionElapsedTime]);

  // Show welcome screen with microphone check and settings
  if (showWelcomeScreen) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-2xl w-full space-y-8">
          <div className="space-y-4">
            <h1 className="text-4xl font-bold tracking-tight">
              Ready for Your Interview! 🎙️
            </h1>
            <p className="text-xl text-muted-foreground">
              You'll be interviewed by our AI assistant. Please speak clearly into your microphone.
            </p>
            
            {/* Information badge about timings */}
            <div className="mt-3 p-4 rounded-lg border bg-slate-50">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                <span className="text-sm font-semibold text-slate-800">Important timings</span>
              </div>
              <div className="overflow-hidden rounded-md border bg-white">
                <table className="w-full text-sm">
                  <tbody>
                    <tr className="border-b">
                      <td className="px-3 py-2 text-slate-600">Total interview time</td>
                      <td className="px-3 py-2 font-medium text-slate-800 text-right">15 minutes</td>
                    </tr>
                    <tr className="border-b">
                      <td className="px-3 py-2 text-slate-600">Answer window</td>
                      <td className="px-3 py-2 font-medium text-slate-800 text-right">20 seconds</td>
                    </tr>
                    <tr className="border-b">
                      <td className="px-3 py-2 text-slate-600">Auto-submit after silence</td>
                      <td className="px-3 py-2 font-medium text-slate-800 text-right">3 seconds</td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2 text-slate-600">No response timeout</td>
                      <td className="px-3 py-2 font-medium text-slate-800 text-right">15 seconds</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Microphone check section (compact) */}
            <div className="mt-6 p-4 border rounded-lg bg-muted/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${microphoneActive ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
                  <div>
                    <h2 className="text-[14px] font-semibold">Microphone</h2>
                    <p className="text-[14px] text-muted-foreground">
                      {microphoneActive ? 'Working properly' : 'Allow access to continue'}
                    </p>
                  </div>
                </div>
                {!microphoneActive && (
                  <Button onClick={checkMicrophone} className="h-8 px-3" variant="outline" size="sm">Enable</Button>
                )}
              </div>
              {microphoneActive && (
                <div className="mt-3">
                  <div className="bg-gray-200 rounded-full h-2 w-full overflow-hidden">
                    <div className="bg-green-500 h-full transition-all duration-100" style={{ width: `${audioLevel}%` }} />
                  </div>
                  <p className="text-[12px] text-muted-foreground mt-1">Speak to test your microphone (Level: {audioLevel}%)</p>
                </div>
              )}
            </div>
            
            {/* Response Mode (accordion) */}
            <details className="mt-6 border rounded-lg bg-muted/30">
              <summary className="cursor-pointer px-4 py-2 text-[14px] font-semibold select-none">Response Mode</summary>
              <div className="p-4 pt-2 space-y-3">
                <div className="flex items-center space-x-3">
                  <input
                    type="radio"
                    id="auto-send"
                    name="send-mode"
                    checked={autoSendMode}
                    onChange={() => setAutoSendMode(true)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <label htmlFor="auto-send" className="text-[14px]">
                    <span className="font-medium">Auto Send</span>
                    <span className="text-muted-foreground block text-[14px]">Automatically send after 3s of silence</span>
                  </label>
                </div>
                <div className="flex items-center space-x-3">
                  <input
                    type="radio"
                    id="manual-send"
                    name="send-mode"
                    checked={!autoSendMode}
                    onChange={() => setAutoSendMode(false)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <label htmlFor="manual-send" className="text-[14px]">
                    <span className="font-medium">Manual Send</span>
                    <span className="text-muted-foreground block text-[14px]">Click "Send" when finished</span>
                  </label>
                </div>
              </div>
            </details>
            
            <p className="text-muted-foreground mt-4">
              This interview will use your microphone to analyze your responses. Find a quiet place
              and make sure you're ready to engage in a professional conversation.
            </p>
          </div>
          
          <Button 
            onClick={startInterview} 
            size="lg" 
              className="w-full bg-[#164a7a] hover:bg-[#2d6dad] text-white"
            disabled={!microphoneActive}
          >
            {microphoneActive ? (
              <>Begin Audio Interview <ArrowRight className="ml-2 h-4 w-4" /></>
            ) : (
              "Enable Microphone to Continue"
            )}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-[calc(100vh-0px)] w-full flex-col items-center justify-between bg-white">
      {/* Timer and warning notification */}
      <div className="flex w-full items-center justify-between p-4">
        <div className="flex items-center gap-2">
          {/* Session timer */}
          {sessionStartTime && (
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full border ${
              showTimeWarning 
                ? 'bg-red-50 border-red-300 text-red-700' 
                : sessionElapsedTime > CONSTANTS.SESSION_WARNING_TIME * 0.8 
                ? 'bg-amber-50 border-amber-300 text-amber-700'
                : 'bg-slate-100 border-slate-300 text-slate-700'
            }`}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm font-medium">
                {formatTime(sessionElapsedTime)}
              </span>
            </div>
          )}
        </div>
        
        {/* Warning notification */}
        {showTimeWarning && (
          <div className="flex items-center gap-3 px-4 py-2 rounded-lg bg-red-50 border border-red-300 shadow-sm">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-red-700">
                3 minutes remaining
              </span>
            </div>
            <button
              onClick={() => setShowTimeWarning(false)}
              className="text-red-600 hover:text-red-800"
              aria-label="Dismiss warning"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
      </div>

      <div className="flex flex-1 items-center justify-center">
        <AnimatedOrb isActive={isListening} isMuted={isMuted} isSpeaking={isSpeaking} level={isMuted ? 0 : audioLevel} />
      </div>

      <div className="z-10 mb-6 flex flex-col items-center gap-4">
        <div className="bg-black/10 rounded-full h-2 w-64 overflow-hidden">
          <div className="bg-emerald-500 h-full transition-all" style={{ width: `${isMuted ? 0 : audioLevel}%` }} />
        </div>
        
        {/* Answer window timer - shows after AI finishes speaking */}
        {answerWindowActive && !isSubmitting && (
          <div className={`flex items-center gap-2 text-sm px-4 py-2 rounded-full shadow-sm border ${
            answerWindowRemaining <= 5000 
              ? 'bg-red-50 border-red-300 text-red-700'
              : answerWindowRemaining <= 10000
              ? 'bg-orange-50 border-orange-300 text-orange-700'
              : 'bg-blue-50 border-blue-300 text-blue-700'
          }`}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="font-medium">
              Answer time: {Math.ceil(answerWindowRemaining / 1000)}s
            </span>
          </div>
        )}
        
        {/* Auto mode countdown indicator */}
        {/* {autoSendMode && silenceCountdown > 0 && !isSpeaking && (
          <div className="flex items-center gap-2 text-sm text-gray-600 bg-white/80 px-4 py-2 rounded-full shadow-sm">
            <div className={`w-2 h-2 rounded-full animate-pulse ${silenceCountdown <= 1 ? 'bg-red-500' : 'bg-orange-400'}`}></div>
            <span>Auto-sending in: {silenceCountdown}s</span>
          </div>
        )} */}
        
        {/* Manual mode indicator */}
        {!autoSendMode && isListening && currentTranscription && currentTranscription.trim() && !isSubmitting && (
          <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50/90 px-4 py-2 rounded-full shadow-sm border border-blue-200">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            <span>Manual mode - Click send button when done talking</span>
          </div>
        )}
        {/* TTS playing indicator */}
        {/* {isSpeaking && (
          <div className="flex items-center gap-2 text-sm text-blue-600">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
            <span>AI is speaking...</span>
          </div>
        )} */}
        {/* Muted indicator */}
        {/* {isMuted && !isSpeaking && (
          <div className="flex items-center gap-2 text-sm text-red-600">
            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
            <span>Microphone muted</span>
          </div>
        )} */}
        {/* Silence countdown timer - only show in auto mode */}
        {/* {autoSendMode && silenceCountdown > 0 && !isSpeaking && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <div className={`w-2 h-2 rounded-full animate-pulse ${silenceCountdown <= 1 ? 'bg-red-500' : 'bg-orange-400'}`}></div>
            <span>Silence timer: {silenceCountdown}s</span>
            {silenceCountdown <= 1 && (
              <span className="text-xs text-red-500 font-medium">(submitting...)</span>
            )}
            {!userTalkedAfterAI && silenceCountdown > 1 && (
              <span className="text-xs text-gray-500">(waiting for response)</span>
            )}
          </div>
        )} */}
        
        {/* Manual mode indicator
        {!autoSendMode && isListening && currentTranscription && currentTranscription.trim() && (
          <div className="flex items-center gap-2 text-sm text-blue-600">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            <span>Manual mode - Click send when ready</span>
          </div>
        )} */}
        <div className="flex items-center gap-6">
          <button
            type="button"
            onClick={toggleMute}
            className={["inline-flex h-14 w-14 items-center justify-center rounded-full","border border-slate-200 bg-white text-slate-700 shadow-sm","hover:bg-slate-50 active:scale-95 transition"].join(" ")}
            aria-pressed={isMuted}
          >
            {isMuted ? (
              <MicOff className="h-5 w-5 text-rose-600" />
            ) : (
              <Mic className="h-5 w-5 text-slate-700" />
            )}
          </button>
          
          {/* Send button - only show in manual mode when there's transcription */}
          {!autoSendMode && currentTranscription && currentTranscription.trim() && (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className={["inline-flex h-14 w-14 items-center justify-center rounded-full","border border-slate-200 bg-blue-600 text-white shadow-sm","hover:bg-blue-700 active:scale-95 transition","disabled:opacity-50 disabled:cursor-not-allowed"].join(" ")}
              aria-label="Done Talking"
              title="Done Talking - Click to send your response"
            >
              {isSubmitting ? (
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              )}
            </button>
          )}
          {!microphoneActive && (
            <div className="flex flex-col items-center gap-3 max-w-md text-center">
              <Button onClick={checkMicrophone} className="h-14 px-6">Enable Mic</Button>
              {permission === "denied" ? (
                <div className="text-xs text-slate-600 space-y-2">
                  <p>Microphone is blocked. Please allow access in your browser, then click Enable Mic again.</p>
                  <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-amber-900">
                    <p className="font-medium">How to allow microphone:</p>
                    <ul className="mt-1 list-disc space-y-1 pl-5 text-left">
                      <li>Find the lock icon near the address bar.</li>
                      <li>Open Site settings and set Microphone to Allow.</li>
                      <li>Reload this page if prompted.</li>
                    </ul>
                  </div>
                  <div className="flex flex-wrap items-center justify-center gap-3 text-[11px]">
                    <a href="chrome://settings/content/microphone" className="underline" target="_blank" rel="noreferrer">Chrome settings</a>
                    <a href="edge://settings/content/microphone" className="underline" target="_blank" rel="noreferrer">Edge settings</a>
                    <a href="about:preferences#privacy" className="underline" target="_blank" rel="noreferrer">Firefox settings</a>
                    <a href="https://support.apple.com/guide/safari/safo1062/mac" className="underline" target="_blank" rel="noreferrer">Safari guide</a>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-slate-500">We need microphone access. Click Enable Mic to continue.</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* <div className="w-full max-w-3xl px-4 pb-8">
        <div className="rounded-xl border border-slate-200 bg-white/70 p-3 shadow-sm">
          <div className="max-h-48 space-y-2 overflow-auto p-1">
            {chatHistory.length === 0 ? (
              <p className="text-center text-sm text-slate-500">{placeholder}</p>
            ) : (
              chatHistory.map((line, idx) => (
                <p key={idx} className={line.role === "assistant" ? "text-slate-700" : "text-slate-900 font-medium"}>
                  {line.content || (typeof line === "string" ? line : "")}
                </p>
              ))
            )}
          </div>
          {process.env.NODE_ENV === 'development' && (
          <div className="mt-3 flex items-center gap-2">
            <input
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              placeholder="Speak or type your message…"
              className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
              autoComplete="off"
            />
            <Button onClick={handleSubmit} disabled={isSubmitting || !(textInput || currentTranscription)} className="px-4">
              {isSubmitting ? "Sending…" : "Send"}
            </Button>
          </div>
          )}
        </div>
      </div> */}
    </div>
  );
}

export default AudioInterview;


